import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useAuth } from "../helper/useAuth";
import { httpService } from "../../services/httpService";
import { API_CONFIG } from "../../shared/constants";
import { IChartData, IChartResponse } from "../../features/Apps/interface";

type BaseChartDataParams = {
  appSlug: string;
  startDate: Date;
  endDate: Date;
};

type CountryChartDataParams = BaseChartDataParams & {
  countryCode: string;
};

export const useAppChartData = (params: BaseChartDataParams) => {
  const { selectedIntegration } = useAuth();
  const { appSlug, startDate, endDate } = params;

  // Format dates for query key to ensure it updates when dates change
  const fromDate = dayjs(startDate).format("YYYY-MM-DD");
  const toDate = dayjs(endDate).format("YYYY-MM-DD");

  return useQuery({
    queryKey: [
      "appChartData",
      selectedIntegration?.value,
      appSlug,
      fromDate,
      toDate,
    ],
    queryFn: async () => {
      const response = await httpService.get<IChartResponse>(
        API_CONFIG.path.getAppsChart,
        {
          from: fromDate,
          to: toDate,
          app: appSlug,
          tracking_id: selectedIntegration?.value as string,
        }
      );
      // ... labels logic etc

      if (!response.labels.length) {
        throw new Error("No chart data available");
      }

      const formattedData: IChartData[] = response.labels.map(
        (date, index) => ({
          date,
          estimateRevenue: response.estimateRevenue[index],
          totalCost_with_tax: response.totalCost_with_tax[index],
          iapRevenue: response.iapRevenue[index],
          totalCost_without_tax: response.totalCost_without_tax[index],
          totalNet_with_tax: response.totalNet_with_tax[index],
          totalNet_without_tax: response.totalNet_without_tax[index],
        })
      );

      return formattedData;
    },
    // Only fetch if we have a selected integration and app slug
    enabled: !!selectedIntegration?.value && !!appSlug,
  });
};

export const useCountryChartData = (params: CountryChartDataParams) => {
  const { selectedIntegration } = useAuth();
  const { appSlug, startDate, endDate, countryCode } = params;

  // Format dates for query key to ensure it updates when dates change
  const fromDate = dayjs(startDate).format("YYYY-MM-DD");
  const toDate = dayjs(endDate).format("YYYY-MM-DD");

  return useQuery({
    queryKey: [
      "countryChartData",
      selectedIntegration?.value,
      appSlug,
      fromDate,
      toDate,
      countryCode,
    ],
    queryFn: async () => {
      const response = await httpService.get<IChartResponse>(
        API_CONFIG.path.getAppsCountryChart,
        {
          from: fromDate,
          to: toDate,
          app: appSlug,
          tracking_id: selectedIntegration?.value as string,
          country_code: countryCode,
        }
      );
      // ... labels logic etc

      const allRevenueZero = response.estimateRevenue.every(
        (value) => value === 0
      );
      const allCostZero = response.totalCost_with_tax.every(
        (value) => value === 0
      );
      const allCostWithoutTaxZero = response.totalCost_without_tax.every(
        (value) => value === 0
      );
      const allIapRevenueZero = response.iapRevenue.every(
        (value) => value === 0
      );

      if (
        !response.labels.length ||
        (allRevenueZero && allCostZero && allCostWithoutTaxZero && allIapRevenueZero)
      ) {
        throw new Error("No chart data available");
      } else {
        const formattedData: IChartData[] = response.labels.map(
          (date, index) => ({
            date,
            estimateRevenue: response.estimateRevenue[index],
            totalNet_with_tax: response.totalNet_with_tax[index],
            totalNet_without_tax: response.totalNet_without_tax[index],
            iapRevenue: response.iapRevenue[index],
            totalCost_with_tax: response.totalCost_with_tax[index],
            totalCost_without_tax: response.totalCost_without_tax[index],
          })
        );

        return formattedData;
      }


    },
    // Only fetch if we have a selected integration, app slug, and country code
    enabled: !!selectedIntegration?.value && !!appSlug && !!countryCode,
  });
};
